/**
 * Smart Sessions™ — session packages tab for wellness tenants.
 * Auto-creates a linked Smart Billing™ payment when price > 0.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Package, Plus, Loader2, CheckCircle2, AlertTriangle, Calendar,
  Wallet, Receipt, MinusCircle, Trash2, Sparkles,
} from "lucide-react";

const fmtEUR = (n) => n == null ? "—" : `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : null;

const STATUS_META = {
  active:    { label: "Attivo",     cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", icon: CheckCircle2 },
  completed: { label: "Completato", cls: "bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300",         icon: CheckCircle2 },
  expired:   { label: "Scaduto",    cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",         icon: AlertTriangle },
};

const SessionPackagesTab = ({ clientId }) => {
  const [items,    setItems]    = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [openNew,  setOpenNew]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/wellness/packages/${clientId}`, { withCredentials: true });
      setItems(r.data.packages || []);
      setSummary(r.data.summary || null);
    } catch {
      toast.error("Errore caricamento pacchetti");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="session-packages-tab">
      {/* HERO */}
      <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-violet-500/5 via-transparent to-cyan-500/5 dark:from-violet-500/10 dark:to-cyan-500/10 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow">
              <Package className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-500">Smart Sessions</p>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Pacchetti sedute</h3>
            </div>
          </div>
          <Button
            onClick={() => setOpenNew(true)}
            className="rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white"
            data-testid="package-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nuovo pacchetto
          </Button>
        </div>
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MiniStat label="Attivi"     value={summary.active}     accent="emerald" testid="pkg-stat-active" />
            <MiniStat label="Completati" value={summary.completed}  accent="slate"   testid="pkg-stat-completed" />
            <MiniStat label="Scaduti"    value={summary.expired}    accent="amber"   testid="pkg-stat-expired" />
            <MiniStat label="Sedute rimaste" value={summary.remaining_total} accent="violet" testid="pkg-stat-remaining" big />
          </div>
        )}
      </div>

      {/* LIST */}
      {loading ? (
        <div className="space-y-2">
          {[0,1].map(i => <div key={i} className="h-24 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center" data-testid="packages-empty">
          <div className="mx-auto w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-100 to-cyan-100 dark:from-violet-900/30 dark:to-cyan-900/30 flex items-center justify-center mb-2">
            <Sparkles className="w-5 h-5 text-violet-500" />
          </div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Nessun pacchetto ancora</p>
          <p className="text-xs text-slate-400 mt-1">Crea il primo pacchetto per iniziare a tracciare le sedute.</p>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="packages-list">
          {items.map(p => <PackageCard key={p.package_id} p={p} onChanged={load} />)}
        </ul>
      )}

      <NewPackageDialog open={openNew} onOpenChange={setOpenNew} clientId={clientId} onCreated={() => { setOpenNew(false); load(); }} />
    </div>
  );
};

const MiniStat = ({ label, value, accent, testid, big }) => {
  const cls = {
    emerald: "from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 text-emerald-700 dark:text-emerald-300",
    amber:   "from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 text-amber-700 dark:text-amber-300",
    slate:   "from-slate-50 to-slate-100 dark:from-white/5 dark:to-white/10 text-slate-500",
    violet:  "from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 text-violet-700 dark:text-violet-300",
  }[accent];
  return (
    <div className={`rounded-xl bg-gradient-to-br ${cls} p-2.5 border border-slate-200/60 dark:border-white/10`} data-testid={testid}>
      <p className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</p>
      <p className={`font-black text-slate-900 dark:text-white ${big ? "text-2xl" : "text-lg"} mt-0.5 font-variant-numeric tabular-nums`}>{value}</p>
    </div>
  );
};

const PackageCard = ({ p, onChanged }) => {
  const meta = STATUS_META[p.status] || STATUS_META.active;
  const Icon = meta.icon;
  const [busy, setBusy] = useState(false);

  const remaining = p.remaining_sessions;
  const progress  = p.progress_pct;

  const consume = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/wellness/packages/${p.package_id}/consume`, {}, { withCredentials: true });
      toast.success("Seduta scaricata");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare il pacchetto "${p.name}"? L'azione è irreversibile.`)) return;
    try {
      await axios.delete(`${API}/wellness/packages/${p.package_id}`, { withCredentials: true });
      toast.success("Pacchetto eliminato");
      onChanged();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <li
      className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4 hover:border-violet-300 transition-all"
      data-testid={`package-card-${p.package_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-slate-900 dark:text-white">{p.name}</h4>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${meta.cls}`}>
              <Icon className="w-2.5 h-2.5" /> {meta.label}
            </span>
            {p.payment_id && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-violet-500">
                <Receipt className="w-2.5 h-2.5" /> billing
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {p.total_sessions} sedute · {fmtEUR(p.price)}
            {p.expires_at && <> · Scadenza {fmtDate(p.expires_at)}</>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Sedute rimaste</p>
          <p className="font-black text-2xl text-slate-900 dark:text-white font-variant-numeric tabular-nums">
            {remaining}<span className="text-slate-400 text-sm font-normal">/{p.total_sessions}</span>
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="mt-3">
        <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
            data-testid={`package-progress-${p.package_id}`}
          />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">{p.used_sessions}/{p.total_sessions} sedute utilizzate ({progress}%)</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {p.status === "active" && (
          <Button
            size="sm"
            onClick={consume}
            disabled={busy || remaining === 0}
            className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white text-[11px]"
            data-testid={`package-consume-${p.package_id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <MinusCircle className="w-3 h-3 mr-1" />}
            Scarica seduta
          </Button>
        )}
        {p.notes && (
          <span className="text-[10px] italic text-slate-400 max-w-md truncate">"{p.notes}"</span>
        )}
        <Button
          size="sm" variant="ghost"
          onClick={remove}
          className="rounded-full ml-auto text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 h-7 w-7 p-0"
          data-testid={`package-delete-${p.package_id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </li>
  );
};

const NewPackageDialog = ({ open, onOpenChange, clientId, onCreated }) => {
  const [name,  setName]  = useState("");
  const [tot,   setTot]   = useState(10);
  const [price, setPrice] = useState("");
  const [exp,   setExp]   = useState("");
  const [notes, setNotes] = useState("");
  const [createPay, setCreatePay] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName("Pacchetto 10 sedute fisioterapia"); setTot(10); setPrice(""); setExp(""); setNotes(""); setCreatePay(true); }
  }, [open]);

  const submit = async () => {
    if (!name.trim() || tot < 1) {
      toast.error("Nome e numero sedute obbligatori");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/packages/${clientId}`, {
        name:           name.trim(),
        total_sessions: Number(tot),
        price:          price ? Number(price) : null,
        expires_at:     exp || null,
        notes:          notes.trim() || null,
        create_payment: createPay,
      }, { withCredentials: true });
      toast.success("Pacchetto creato");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="package-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
              <Package className="w-3.5 h-3.5 text-white" />
            </div>
            Nuovo pacchetto
          </DialogTitle>
          <DialogDescription>
            Crea un pacchetto di sedute. Se inserisci un prezzo, viene generata anche la ricevuta nel modulo Smart Billing™.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nome pacchetto</label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="package-new-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Sedute totali</label>
              <Input type="number" min="1" max="200" value={tot} onChange={e => setTot(e.target.value)} data-testid="package-new-total" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Prezzo (€)</label>
              <Input type="number" step="0.01" min="0" value={price} placeholder="Opzionale" onChange={e => setPrice(e.target.value)} className="font-mono" data-testid="package-new-price" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Scadenza (opzionale)</label>
            <Input type="date" value={exp} onChange={e => setExp(e.target.value)} data-testid="package-new-expires" />
          </div>
          <Textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Note (opzionali)"
            className="text-sm min-h-[60px]"
            data-testid="package-new-notes"
          />
          {price && Number(price) > 0 && (
            <label className="flex items-center gap-2 p-2 rounded-xl bg-violet-50/60 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-900/40 cursor-pointer">
              <input type="checkbox" checked={createPay} onChange={e => setCreatePay(e.target.checked)} className="accent-violet-600" data-testid="package-new-create-payment" />
              <span className="text-xs font-semibold text-violet-700 dark:text-violet-200 inline-flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Genera anche ricevuta in Smart Billing
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-gradient-to-r from-violet-600 to-cyan-500 text-white"
            data-testid="package-new-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea pacchetto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionPackagesTab;
